import type { Action, ThunkAction } from "@reduxjs/toolkit";
import { combineSlices, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import { artesianApiSlice } from "../features/artesian/artesianApi";
import { globalLoadingSlice } from "../features/globalLoadingBar/globalLoadingApi";
import { authSlice } from "../lib/authentication/authenticationSlice";
import { envSlice } from "../lib/authentication/envSlice";
import type { AuthProvider } from "../lib/authentication/providers/authProviderInterface";
import errorReducer from "../lib/errorHandler/errorHandler";
import { curveDataSlice } from "../store/curveDataSlice";
import { permissionsSlice } from "../store/permissionSlice";
import selectedRowsReducer from "../store/selectedRowsSlice";

// `combineSlices` automatically combines the reducers using
// their `reducerPath`s, therefore we no longer need to call `combineReducers`.
const sliceReducers = combineSlices(authSlice, envSlice, curveDataSlice, artesianApiSlice, globalLoadingSlice, {
  errorHandler: errorReducer,
  selectedRows: selectedRowsReducer,
  curveData: curveDataSlice.reducer,
  permissions: permissionsSlice.reducer,
});

// Infer the `RootState` type from the root reducer
export type RootState = ReturnType<typeof sliceReducers>;

export function initStore(authProviderInstance: AuthProvider) {
  const store = configureStore({
    reducer: sliceReducers,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: {
            authProvider: authProviderInstance,
          },
        },
      }).concat(artesianApiSlice.middleware),
  });

  setupListeners(store.dispatch);
  return store;
}

export const resetApiActions = [artesianApiSlice.util.resetApiState()];

export type ExtraType = {
  authProvider: AuthProvider;
};

export type AppStore = ReturnType<typeof initStore>;
// Infer the `AppDispatch` type from the store itself
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ThunkReturnType = void> = ThunkAction<ThunkReturnType, RootState, ExtraType, Action>;
